import React, { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  style,
  ...props
}) => {
  const getVariantStyle = (): React.CSSProperties => {
    switch (variant) {
      case 'primary':
        return {
          background: 'linear-gradient(180deg, var(--gabmic-orange-strong), var(--gabmic-orange))',
          color: 'var(--bg-background)', // Dark text on orange for high contrast
          borderColor: 'var(--gabmic-orange)',
          boxShadow: 'var(--shadow-orange)',
        };
      case 'secondary':
        return {
          background: 'linear-gradient(145deg, rgba(255,255,255,0.055), rgba(255,255,255,0.012)), var(--bg-surface)',
          color: 'var(--text-main)',
          borderColor: 'var(--border-color)',
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: 'var(--text-main)',
          borderColor: 'var(--border-color)',
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: 'var(--text-secondary)',
          borderColor: 'transparent',
        };
      default:
        return {};
    }
  };

  const getSizeStyle = (): React.CSSProperties => {
    switch (size) {
      case 'sm': 
        return { padding: '0.5rem 1.25rem', fontSize: '0.85rem' };
      case 'md': 
        return { padding: '0.75rem 1.75rem', fontSize: '1rem' };
      case 'lg': 
        return { padding: '1rem 2.5rem', fontSize: '1.125rem' };
      default: 
        return {};
    }
  };

  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.45rem',
    borderRadius: '8px',
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
    textTransform: 'none',
    letterSpacing: 0,
    width: fullWidth ? '100%' : 'auto',
    borderStyle: 'solid',
    borderWidth: '1px',
    outline: 'none',
    ...getVariantStyle(),
    ...getSizeStyle(),
    ...style,
  };

  return (
    <button 
      style={baseStyle} 
      className={`gabmic-button ${className}`}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        if (variant === 'primary') {
          e.currentTarget.style.borderColor = '#E66E00';
          e.currentTarget.style.boxShadow = '0 16px 34px rgba(255, 122, 0, 0.26)';
        } else if (variant === 'secondary' || variant === 'outline') {
          e.currentTarget.style.borderColor = 'var(--text-secondary)';
        }
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        if (variant === 'primary') {
          e.currentTarget.style.borderColor = 'var(--gabmic-orange)';
          e.currentTarget.style.boxShadow = 'var(--shadow-orange)';
        } else if (variant === 'secondary' || variant === 'outline') {
          e.currentTarget.style.borderColor = 'var(--border-color)';
        }
      }}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
